import { GovernanceSection } from "~/components/company/governance-section";
import { RelatedPartiesSection } from "~/components/company/related-parties-section";
import { PeopleTab } from "~/components/company/tabs/people-tab";
import { authHeadersFromRequest } from "~/lib/auth-headers.server";
import {
	companyCanonicalUrl,
	companyFromMatches,
	companyNotFoundMeta,
	companySocialMeta,
	fetchCompanySection,
	parseIcoSlug,
	useCompanyProfile,
} from "~/lib/company-profile";
import { envConfig } from "~/lib/env-validation";
import { generateMetaTags } from "~/lib/seo";
import type { GovernanceResponse, RelatedPartiesResponse } from "~/types/corporate";
import type { Route } from "./+types/lide";

// --- Loader ---

export async function loader({ params, request }: Route.LoaderArgs) {
	const ico = parseIcoSlug(params.icoSlug);
	// Tier-aware endpoints — SSR loaders must recover the bearer token from cookies.
	const headers = await authHeadersFromRequest(request);

	// Section fetches tolerate failure individually (null → section hides).
	const [governance, relatedParties] = await Promise.all([
		fetchCompanySection<GovernanceResponse>(
			`${envConfig.apiBaseUrl}/companies/${ico}/governance`,
			headers,
		).catch(() => null),
		fetchCompanySection<RelatedPartiesResponse>(
			`${envConfig.apiBaseUrl}/companies/${ico}/related-parties`,
			headers,
		).catch(() => null),
	]);

	return { governance, relatedParties };
}

// --- Meta ---

export function meta({ matches }: Route.MetaArgs) {
	const company = companyFromMatches(matches);
	if (!company) return companyNotFoundMeta();

	return generateMetaTags({
		...companySocialMeta(company),
		title: `Majitelé a jednatelé – ${company.name} | FirmIndex`,
		description: `Statutární orgány, společníci a vlastnická struktura firmy ${company.name} (IČO: ${company.ico}) z obchodního rejstříku.`,
		canonical: companyCanonicalUrl(company, "lide"),
		noindex: true,
	});
}

export function headers() {
	return { "X-Robots-Tag": "noindex, nofollow" };
}

// --- Component ---

export default function CompanyLideRoute({ loaderData }: Route.ComponentProps) {
	const company = useCompanyProfile();

	return (
		<div className="flex flex-col gap-6">
			<PeopleTab
				officers={company.officers}
				shareholders={company.shareholders}
				officersInactiveCount={company.officersInactiveCount}
				shareholdersInactiveCount={company.shareholdersInactiveCount}
				viewerTier={company.viewerTier}
				zpusobJednani={company.zpusobJednani}
				pocetClenu={company.pocetClenu}
			/>
			<GovernanceSection response={loaderData.governance} />
			<RelatedPartiesSection response={loaderData.relatedParties} />
		</div>
	);
}
